import React, { useRef, useEffect } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

interface VideoJSPlayerProps {
  src: string;
  poster?: string;
}

export const VideoJSPlayer: React.FC<VideoJSPlayerProps> = ({ src, poster }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<videojs.Player | null>(null);

  useEffect(() => {
    if (!containerRef.current || playerRef.current) return;

    const videoElement = document.createElement('video-js');
    videoElement.classList.add('vjs-default-skin');
    videoElement.style.width = '100%';
    videoElement.style.height = '100%';
    containerRef.current.appendChild(videoElement);

    playerRef.current = videojs(videoElement, {
      controls: true,
      autoplay: false,
      preload: 'none',
      poster,
      sources: [{ src, type: 'application/x-mpegURL' }],
    });

    return () => {
      const player = playerRef.current;
      if (!player || player.isDisposed()) return;

      try {
        player.dispose();
      } catch (error) {
        // React may remove the video node first when toggling preview quickly.
        console.warn('Video.js disposal skipped due to stale DOM node.', error);
      } finally {
        playerRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || player.isDisposed()) return;

    player.poster(poster || '');
    player.src([{ src, type: 'application/x-mpegURL' }]);
  }, [src, poster]);

  return (
    <div data-vjs-player style={{ width: '100%', height: '100%' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};